import { format, isSameDay, startOfDay } from 'date-fns';
import { parseDateOnly } from '@/lib/dateOnly';

export const PO_TABLE_COLUMNS = [
  'Files',
  'Manufacturer',
  'Purchase Date',
  'Uploaded Date',
  'Uploaded By',
  'Status',
] as const;

export type POTableColumn = (typeof PO_TABLE_COLUMNS)[number];

export const PO_LIST_LAYOUT_MODES = ['list', 'grouped'] as const;
export type POListLayoutMode = (typeof PO_LIST_LAYOUT_MODES)[number];

/** Columns that use a date-range calendar in the filter UI. */
export const PO_DATE_FILTER_COLUMNS: POTableColumn[] = ['Purchase Date', 'Uploaded Date'];

const statusLabels: Record<string, string> = {
  pending_upload: 'Pending Upload',
  uploaded: 'Uploaded',
  under_review: 'Under Review',
  funded: 'Funded',
  rejected: 'Rejected',
  archived: 'Archived',
  pending: 'Uploaded',
};

export const normalizePOStatus = (status: string): string => {
  if (status === 'pending') return 'uploaded';
  return status;
};

/** Resellers may only edit POs while they remain in the uploaded state (incl. legacy `pending`). */
export const canResellerEditPurchaseOrder = (status: string): boolean =>
  normalizePOStatus(status) === 'uploaded';

export const getResellerPOEditBlockedMessage = (status: string): string => {
  const normalized = normalizePOStatus(status);
  const statusLabel =
    statusLabels[status] || statusLabels[normalized] || normalized.replace(/_/g, ' ');
  return `This purchase order cannot be edited while it's in ${statusLabel}.`;
};

export interface POForTableRow {
  id: string;
  status: string;
  vendor_name: string;
  manufacturer_name: string | null;
  purchase_date: string | null;
  created_at: string;
  created_by_email: string | null;
  uploaded_by_email: string | null;
  file_uploaded_at: string | null;
  file_name: string | null;
  files?: Array<{ file_name: string; manufacturer_name: string | null }>;
}

export function getPODisplayManufacturer(po: POForTableRow): string {
  const fileManufacturers = (po.files || [])
    .map((f) => f.manufacturer_name)
    .filter((m): m is string => !!m);
  const uniqueManufacturers = [...new Set(fileManufacturers)];

  if (uniqueManufacturers.length === 0) {
    return po.manufacturer_name || po.vendor_name || '-';
  }
  if (uniqueManufacturers.length === 1) {
    return uniqueManufacturers[0];
  }
  return `${uniqueManufacturers[0]} +${uniqueManufacturers.length - 1} more`;
}

export function getPOFilesDisplay(po: POForTableRow): string {
  const primaryFile = po.files?.[0]?.file_name ?? po.file_name;
  if (!primaryFile) return '-';
  const fileCount = (po.files?.length || 0) + (po.file_name ? 1 : 0);
  if (fileCount > 1) {
    return `${primaryFile} (+${fileCount - 1} more)`;
  }
  return primaryFile;
}

/**
 * Value for the edit form. A stored purchase or order date stays on its
 * calendar day. A missing purchase date falls back to the upload instant,
 * the same instant the list formats, so saving does not write a different day.
 */
export function purchaseDateForEdit(po: {
  purchase_date: string | null;
  file_uploaded_at: string | null;
  order_date: string | null;
}): Date | undefined {
  if (po.purchase_date) return parseDateOnly(po.purchase_date) ?? undefined;
  if (po.file_uploaded_at) return new Date(po.file_uploaded_at);
  if (po.order_date) return parseDateOnly(po.order_date) ?? undefined;
  return undefined;
}

function formatPurchaseDateForFilter(po: POForTableRow): string {
  if (po.purchase_date) {
    const date = parseDateOnly(po.purchase_date);
    return date ? format(date, 'yyyy-MM-dd') : '-';
  }
  return formatPOUploadedDateForFilter(po);
}

function formatPOUploadedDateForFilter(po: POForTableRow): string {
  const value = po.file_uploaded_at || po.created_at;
  return format(new Date(value), 'yyyy-MM-dd');
}

export function getPOStatusDisplay(po: POForTableRow): string {
  const normalized = normalizePOStatus(po.status);
  return statusLabels[po.status] || statusLabels[normalized] || normalized.replace(/_/g, ' ');
}

export function buildPOTableRowData(po: POForTableRow): Record<POTableColumn, string> {
  return {
    Files: getPOFilesDisplay(po),
    Manufacturer: getPODisplayManufacturer(po),
    'Purchase Date': formatPurchaseDateForFilter(po),
    'Uploaded Date': formatPOUploadedDateForFilter(po),
    'Uploaded By': po.created_by_email || po.uploaded_by_email || '-',
    Status: getPOStatusDisplay(po),
  };
}

function purchaseDateTimestamp(po: POForTableRow): number {
  if (po.purchase_date) {
    return parseDateOnly(po.purchase_date)?.getTime() ?? 0;
  }
  return new Date(po.file_uploaded_at || po.created_at).getTime();
}

function uploadedDateTimestamp(po: POForTableRow): number {
  return new Date(po.file_uploaded_at || po.created_at).getTime();
}

export function comparePurchaseOrders(
  a: POForTableRow,
  b: POForTableRow,
  column: POTableColumn,
  direction: 'asc' | 'desc',
): number {
  let result = 0;

  switch (column) {
    case 'Files':
      result = getPOFilesDisplay(a).localeCompare(getPOFilesDisplay(b), undefined, {
        sensitivity: 'base',
      });
      break;
    case 'Manufacturer':
      result = getPODisplayManufacturer(a).localeCompare(getPODisplayManufacturer(b), undefined, {
        sensitivity: 'base',
      });
      break;
    case 'Purchase Date':
      result = purchaseDateTimestamp(a) - purchaseDateTimestamp(b);
      break;
    case 'Uploaded Date':
      result = uploadedDateTimestamp(a) - uploadedDateTimestamp(b);
      break;
    case 'Uploaded By': {
      const aBy = a.created_by_email || a.uploaded_by_email || '';
      const bBy = b.created_by_email || b.uploaded_by_email || '';
      result = aBy.localeCompare(bBy, undefined, { sensitivity: 'base' });
      break;
    }
    case 'Status':
      result = getPOStatusDisplay(a).localeCompare(getPOStatusDisplay(b), undefined, {
        sensitivity: 'base',
      });
      break;
    default:
      result = 0;
  }

  return direction === 'asc' ? result : -result;
}

/** Uploaded date for layout grouping: file_uploaded_at, else created_at. */
export function getPOUploadedDate(po: POForTableRow): Date {
  return new Date(po.file_uploaded_at || po.created_at);
}

export function partitionPurchaseOrdersByTodayPast<T extends POForTableRow>(
  pos: T[],
  referenceDate: Date = new Date(),
): { today: T[]; past: T[] } {
  const todayStart = startOfDay(referenceDate);
  const today: T[] = [];
  const past: T[] = [];

  for (const po of pos) {
    const uploadedDate = getPOUploadedDate(po);
    if (isSameDay(uploadedDate, referenceDate)) {
      today.push(po);
    } else if (uploadedDate < todayStart) {
      past.push(po);
    } else {
      // Future-dated uploads roll into Today so nothing is hidden
      today.push(po);
    }
  }

  return { today, past };
}
